"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ProductionStills() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const projects = [{
    name: "The Midnight Monologue",
    location: "Film Noir Series",
    year: "2024",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_noir_monologue.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_noir_monologue.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_noir_monologue.png-wvc-sizes",
    alt: "Jordan Rivers in a high-contrast dramatic film still",
    gridClasses: "md:col-start-1 md:col-end-8 md:mt-0",
    aspect: "aspect-[4/5]"
  }, {
    name: "Hamlet: Act III",
    location: "Royal Theater",
    year: "2023",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_hamlet_stage.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_hamlet_stage.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_hamlet_stage.png-wvc-sizes",
    alt: "Jordan Rivers performing on stage in a dramatic theater production",
    gridClasses: "md:col-start-9 md:col-end-13 md:mt-24",
    aspect: "aspect-[3/4]"
  }, {
    name: "Shattered Glass",
    location: "Independent Feature",
    year: "2023",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_shattered_glass_still.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_shattered_glass_still.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_shattered_glass_still.png-wvc-sizes",
    alt: "Cinematic close-up of Jordan Rivers in a dramatic film scene",
    gridClasses: "md:col-start-2 md:col-end-7 md:mt-16",
    aspect: "aspect-[5/4]"
  }, {
    name: "The Last Descent",
    location: "Television Drama",
    year: "2022",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_last_descent_action.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_last_descent_action.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698963/img/jordan_rivers_last_descent_action.png-wvc-sizes",
    alt: "Jordan Rivers in an intense action sequence for television",
    gridClasses: "md:col-start-8 md:col-end-13 md:-mt-8",
    aspect: "aspect-[4/5]"
  }];
  return <section data-section-id="5067" id="production-stills" className="bg-background text-foreground pt-32 pb-24 md:pb-32 overflow-hidden">
      <div className="container mx-auto px-6 md:px-10">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="md:col-span-7">
            <h2 className="font-default font-light text-5xl md:text-7xl lg:text-8xl leading-[1.0] text-foreground tracking-tighter">
              Dramatic
              <br />
              <span className="italic opacity-80">Presence</span>
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9 flex items-end">
            <p className="font-sans text-lg md:text-xl leading-relaxed text-foreground/80 font-light">
              A visual archive of transformative performances across stage and screen. 
              Capturing the raw intensity and quiet vulnerability that defines Jordan 
              Rivers' cinematic and theatrical journey.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className={`relative grid grid-cols-1 md:grid-cols-12 gap-y-10 md:gap-y-16 transition-opacity duration-1000 ${gridVisible ? "opacity-100" : "opacity-0"}`}>
          {projects.map((project, i) => <div data-index={i} key={project.name} className={`group relative ${project.gridClasses} transition-all duration-1000 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
          transitionDelay: `${i * 150}ms`
        }}>
              <Link to="/VisualArchivePage" className="block">
                <div className={`relative overflow-hidden rounded-md ${project.aspect} bg-card border border-border`}>
                  <img src={project.image} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={project.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-[700ms] ease-out group-hover:scale-[1.03]" />
                  {}
                  <div className="absolute bottom-5 left-5 md:bottom-6 md:left-6 right-5 md:right-6 flex items-end justify-between gap-4">
                    <div className="bg-background/90 backdrop-blur-sm rounded-md px-4 py-3 md:px-5 md:py-4 border border-border">
                      <h3 className="font-default italic text-xl md:text-2xl text-foreground leading-none relative inline-block">
                        {project.name}
                        <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-500 ease-out group-hover:w-full" />
                      </h3>
                      <p className="font-mono text-[10px] md:text-xs tracking-widest uppercase text-muted-foreground mt-2">
                        {project.location} — {project.year}
                      </p>
                    </div>
                    <div className="opacity-0 translate-x-[-4px] group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500 bg-primary backdrop-blur-sm rounded-md px-3 py-2 md:px-4 md:py-3">
                      <span className="font-mono text-[10px] md:text-xs tracking-widest uppercase text-primary-foreground inline-flex items-center gap-1.5 whitespace-nowrap">
                        <ArrowRight className="size-3" />
                        Frame
                      </span>
                    </div>
                  </div>
                </div>
              </Link>

              {}
              {i === 1 && <div className="hidden md:block absolute -left-20 lg:-left-24 top-1/2 -translate-y-1/2 pointer-events-none">
                  <span className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground inline-block" style={{
              writingMode: "vertical-rl",
              transform: "rotate(180deg)"
            }}>
                    — ISO 800 / 24FPS
                  </span>
                </div>}
            </div>)}
        </div>

        {}
        <div ref={ctaRef} className={`mt-20 md:mt-28 flex justify-end transition-all duration-1000 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Button asChild variant="outline" size="lg" className="rounded-md border-border bg-transparent text-foreground hover:bg-foreground hover:text-background px-8 py-6 h-auto font-mono text-xs tracking-widest uppercase transition-all duration-500">
            <Link to="/VisualArchivePage">
              View Visual Archive
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}